import { useRouter } from 'next/router';
import { useState, useEffect } from 'react';
import cn from 'classnames';
import { getAsPath } from 'react-scale/lib/utils';

type Props = {
  pages: number;
  currentPage: number;
  isInPage: boolean;
  pageCallback: () => any;
};

const Paginator = ({ pages, currentPage, isInPage, pageCallback }: Props) => {
  const maxPageOffset = pages - 4;

  const [pageNavigationOffset, setPageNavigationOffset] = useState(0);

  const router = useRouter();

  useEffect(() => {
    if (currentPage < 0) {
      router.push(getAsPath(router.asPath, { page: '1' }), undefined, { scroll: false });
    }
  }, [currentPage, pages]);

  useEffect(() => {
    if (currentPage > pages - 1 && pages > 0) {
      router.push(getAsPath(router.asPath, { page: pages.toString() }), undefined, {
        scroll: false
      });
    }
  }, [currentPage, pages]);

  useEffect(() => {
    if (isInPage && !Number.isNaN(currentPage)) {
      if (currentPage <= 2) {
        setPageNavigationOffset(0);
      } else if (currentPage > maxPageOffset) {
        setPageNavigationOffset(maxPageOffset);
      } else {
        setPageNavigationOffset(currentPage - 2);
      }
    }
  }, [isInPage, currentPage, maxPageOffset]);

  async function handlePreviousPage() {
    await router.push(getAsPath(router.asPath, { page: currentPage.toString() }), undefined, {
      scroll: false
    });
    pageCallback();
  }

  async function handleNextPage() {
    await router.push(getAsPath(router.asPath, { page: (currentPage + 2).toString() }), undefined, {
      scroll: false
    });
    pageCallback();
  }

  async function handlePageNumberClick(n: number) {
    await router.push(getAsPath(router.asPath, { page: (n + 1).toString() }), undefined, {
      scroll: false
    });
    pageCallback();
  }

  return (
    <div className="flex items-center mx-auto space-x-5 text-lg max-w-max whitespace-nowrap">
      <button
        type="button"
        className="flex items-center space-x-2 disabled:cursor-not-allowed disabled:opacity-30"
        onClick={handlePreviousPage}
        disabled={currentPage === 0}
      >
        <span className="inline-block mr-1 font-normal w-fit font-unicode">←</span>
        <span>Previous</span>
      </button>
      <div className="space-x-3">
        {[...Array(pages).keys()].map((_page, i) => {
          if (i < pageNavigationOffset - 4) return null;
          if (i > pageNavigationOffset + 8) return null;

          return (
            <button
              type="button"
              aria-label={`To page ${i}`}
              className={cn({ 'text-scale-mauve underline': currentPage === i })}
              key={_page}
              onClick={() => handlePageNumberClick(i)}
              disabled={currentPage === i}
            >
              {i + 1}
            </button>
          );
        })}
      </div>
      <button
        className="flex items-center space-x-2 disabled:cursor-not-allowed disabled:opacity-30"
        type="button"
        onClick={handleNextPage}
        disabled={currentPage + 1 === pages || pages < 1}
      >
        <span>Next</span>
        <span className="inline-block ml-1 font-normal w-fit font-unicode">→</span>
      </button>
    </div>
  );
};

export default Paginator;
